import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit2, Trash2, X, Package } from 'lucide-react';
import { serviceProviderService } from '../services/serviceProviderService';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import type { ServiceProvider, ServiceProviderInsert, ServiceProviderType } from '../types/serviceProvider';
import { SERVICE_PROVIDER_TYPE_CONFIG } from '../types/serviceProvider';
import { log } from '../lib/logger';

export function ServicesManager() {
  const { t } = useLanguage();
  const { showConfirm, toast } = useModal();
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Other' as ServiceProviderType,
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    vat_number: '',
    notes: '',
  });

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await serviceProviderService.getAll();
      setProviders(data);
    } catch (error) {
      log.error('Error loading service providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await serviceProviderService.update(editingProvider.id, formData);
      } else {
        const providerData: ServiceProviderInsert = {
          ...formData,
          contact_name: formData.contact_name || null,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address || null,
          vat_number: formData.vat_number || null,
          notes: formData.notes || null,
        };
        await serviceProviderService.create(providerData);
      }
      await loadProviders();
      handleCloseForm();
    } catch (error) {
      log.error('Error saving service provider:', error);
    }
  };

  const handleEdit = (provider: ServiceProvider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      type: provider.type,
      contact_name: provider.contact_name || '',
      email: provider.email || '',
      phone: provider.phone || '',
      address: provider.address || '',
      vat_number: provider.vat_number || '',
      notes: provider.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    const confirmed = await showConfirm({
      title: 'Delete Service Provider?',
      message: `Are you sure you want to delete ${provider?.name || 'this service provider'}? This action cannot be undone.`,
      confirmText: 'Delete Provider',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await serviceProviderService.delete(id);
      toast.success(t('services.deleteSuccess'));
      await loadProviders();
    } catch (error) {
      log.error('Error deleting service provider:', error);
      toast.error(t('services.deleteFailed'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingProvider(null);
    setFormData({
      name: '',
      type: 'Other',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      vat_number: '',
      notes: '',
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('services.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('services.description')}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {providers.length} {providers.length === 1 ? t('services.service') : t('services.services')}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100">
        <div className="p-6 border-b">
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('services.addNew')}
            </button>
          </div>
        </div>

      <div className="p-6">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Loading service providers...</p>
        ) : providers.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No service providers yet. Add your first one!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((provider) => {
              const typeConfig = SERVICE_PROVIDER_TYPE_CONFIG[provider.type];
              return (
                <div key={provider.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{provider.name}</h3>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${typeConfig.bgColor} ${typeConfig.textColor}`}>
                        {provider.type}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(provider)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(provider.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    {provider.contact_name && (
                      <div>
                        <span className="font-medium text-gray-600">Contact:</span>
                        <span className="ml-2 text-gray-900">{provider.contact_name}</span>
                      </div>
                    )}
                    {provider.email && (
                      <div>
                        <span className="font-medium text-gray-600">Email:</span>
                        <span className="ml-2 text-gray-900">{provider.email}</span>
                      </div>
                    )}
                    {provider.phone && (
                      <div>
                        <span className="font-medium text-gray-600">Phone:</span>
                        <span className="ml-2 text-gray-900">{provider.phone}</span>
                      </div>
                    )}
                    {provider.vat_number && (
                      <div>
                        <span className="font-medium text-gray-600">VAT:</span>
                        <span className="ml-2 text-gray-900">{provider.vat_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingProvider ? 'Edit Service Provider' : 'Add Service Provider'}
              </h3>
              <button onClick={handleCloseForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  >
                    <option value="Videographer">Videographer</option>
                    <option value="Photographer">Photographer</option>
                    <option value="Stylist">Stylist</option>
                    <option value="Product">Product</option>
                    <option value="Location">Location</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                  <input
                    type="text"
                    name="vat_number"
                    value={formData.vat_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
                >
                  {editingProvider ? 'Update' : 'Create'} Service Provider
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
